import * as L from 'leaflet';
import Popper from 'popper.js';
import { findClosestIndex } from './utils/array';
import { createElement } from './utils/dom';
/**
 * The slider component.
 */
export class Slider {
    /**
     * Create a new slider instance.
     * @param args - The slider constructor arguments.
     */
    constructor(args) {
        /**
         * The available ticks.
         */
        this.ticks = [];
        /**
         * Background color of the knob if it displays an icon
         */
        this.knobBgColor = '#67caff';
        this.onStateChangedCb = args.onStateChangedCb;
        this.onPointerDownCb = args.onPointerDownCb;
        this.onPointerMoveCb = args.onPointerMoveCb;
        this.onPointerUpCb = args.onPointerUpCb;
        this.options = Object.assign(Object.assign({}, Slider.optionsDefault), args.options);
        const { elContainer, elTooltip, elKnob, elIcon, popperInstance, trackLeft, trackRight, } = this.createElements();
        this.elContainer = elContainer;
        this.elKnob = elKnob;
        this.elTooltip = elTooltip;
        this.elIcon = elIcon;
        this.popperInstance = popperInstance;
        this.trackLeft = trackLeft;
        this.trackRight = trackRight;
        this.ticks = this.createTicks(args.tickRecords);
        this.state = {
            tooltipOpacity: this.options.isTooltipPermanent ? 1 : 0,
            selectedTick: 0,
            knobPosition: 0,
            threshold: 0,
        };
        if (this.ticks.length > 0) {
            this.updateState({
                selectedTick: args.initialTick,
                knobPosition: this.ticks[args.initialTick].position,
            });
        }
        if (this.state.knobPosition < this.state.threshold) {
            this.elKnob.append(this.elIcon);
        }
    }
    /***
     * reset slider ticks
     * @param tickRecords
     */
    updateTicks(tickRecords) {
        this.ticks = this.createTicks(tickRecords);
    }
    /**
     * Resize visual tracks of slider.
     * @param sizeRight - relative size of right hand side track in percentage
     */
    resizeTracks(sizeRight) {
        if (sizeRight < 0 || sizeRight > 100) {
            throw new Error(`'sizeRight must be in range [0, 100]. Found: ${sizeRight}`);
        }
        this.trackRight.style.width = `${sizeRight}%`;
        this.trackLeft.style.width = `${100 - sizeRight}%`;
        // hack, mutate state directly instead of through this.updateState which breaks everything
        this.state.threshold = sizeRight === 0 ? 101 : 100 - sizeRight;
    }
    /**
     * Construct SliderTick objects (including a position) from tick records
     * @param tickRecords
     * @returns The slider tick objects.
     */
    createTicks(tickRecords) {
        return tickRecords.map((record, index) => (Object.assign(Object.assign({}, record), { 
            // position of the tick in %
            position: (index * 100) / (tickRecords.length - 1) })));
    }
    /**
     * Get the slider's container element with everything rendered inside.
     */
    getContainerElement() {
        return this.elContainer;
    }
    /**
     * Get the slider component's state.
     */
    getState() {
        return this.state;
    }
    /**
     * Set the slider component's state.
     * @param state - The new state to set.
     */
    updateState(state) {
        this.state = Object.assign(Object.assign({}, this.state), state);
        if (!('knobPosition' in state)) {
            this.state['knobPosition'] = this.ticks[this.state.selectedTick].position;
        }
        if ('selectedTick' in state) {
            this.onStateChangedCb(this.ticks[this.state.selectedTick].value);
        }
        this.updateDom();
    }
    /**
     * Move knob one tick to the right.
     */
    moveRight() {
        const nextTickIndex = Math.min(this.state.selectedTick + 1, this.ticks.length - 1);
        this.updateState({ selectedTick: nextTickIndex });
    }
    /**
     * Move knob one tick to the left
     */
    moveLeft() {
        const previousTickIndex = Math.max(0, this.state.selectedTick - 1);
        this.updateState({ selectedTick: previousTickIndex });
    }
    /**
     * Get the available ticks.
     * @returns The available ticks.
     */
    getTicks() {
        return this.ticks;
    }
    /**
     * Create and setup the DOM elements.
     * @returns The created DOM elements.
     */
    createElements() {
        const elContainer = this.createElContainer();
        const elTooltip = Slider.createElTooltip();
        elContainer.appendChild(elTooltip);
        // add slider track consisting of two portions (allows to highlight the forecast part)
        const className = `${Slider.className}__tick`;
        const trackLeft = createElement({
            tagName: 'div',
            classNames: [className, `${className}--accented`],
        });
        const trackRight = createElement({
            tagName: 'div',
            classNames: [className, `${className}--default`],
        });
        trackLeft.style.width = '100%';
        trackRight.style.width = '0%';
        elContainer.appendChild(trackLeft);
        elContainer.appendChild(trackRight);
        const elKnob = this.createElKnob();
        const elIcon = this.createIcon();
        const popperInstance = new Popper(elKnob, elTooltip, {
            placement: 'top',
        });
        elContainer.appendChild(elKnob);
        return {
            elContainer,
            elTooltip,
            elKnob,
            elIcon,
            popperInstance,
            trackLeft,
            trackRight,
        };
    }
    /**
     * Create the container DOM element.
     * @returns The container DOM element.
     */
    createElContainer() {
        const elContainer = createElement({
            tagName: 'div',
            classNames: [Slider.className],
        });
        elContainer.tabIndex = 0;
        L.DomEvent.disableClickPropagation(elContainer);
        L.DomEvent.disableScrollPropagation(elContainer);
        this.setElContainerEventListeners(elContainer);
        return elContainer;
    }
    /**
     * Create the knob DOM element.
     * @returns The knob DOM element.
     */
    createElKnob() {
        const className = `${Slider.className}__knob`;
        const elKnob = createElement({
            tagName: 'div',
            classNames: [className],
        });
        this.setElKnobEventListeners(elKnob);
        return elKnob;
    }
    /**
     * Create the tooltip DOM element.
     * @returns The tooltip DOM element.
     */
    static createElTooltip() {
        const className = `${Slider.className}__tooltip`;
        return createElement({
            tagName: 'div',
            classNames: [className, 'maap_c-floating'],
        });
    }
    createIcon() {
        return createElement({
            tagName: 'div',
            classNames: ['maap_c-icon-container__icon', 'maap_p-measurement__icon'],
        });
    }
    /**
     * Update the DOM according to the component's state.
     */
    updateDom() {
        this.elKnob.style.left = `${this.state.knobPosition}%`;
        this.updateTooltip();
        this.updateIcon();
        this.updateKnob();
    }
    /**
     * Update the tooltip's text and opacity.
     */
    updateTooltip() {
        if (this.state.selectedTick === null) {
            return;
        }
        this.elTooltip.innerHTML = this.ticks[this.state.selectedTick].label;
        this.elTooltip.style.opacity = this.state.tooltipOpacity.toString();
        this.popperInstance.update();
    }
    /**
     * Update the icon and whether it's displayed.
     */
    updateIcon() {
        if (this.state.knobPosition < this.state.threshold) {
            this.elKnob.append(this.elIcon);
        }
        else {
            this.elIcon.remove();
        }
    }
    /**
     * Update the knobs background-color.
     */
    updateKnob() {
        this.elKnob.style.backgroundColor =
            this.state.knobPosition >= this.state.threshold
                ? 'white'
                : this.knobBgColor;
    }
    /**
     * Set the container element's event listeners.
     * @param elContainer - The container DOM element.
     */
    setElContainerEventListeners(elContainer) {
        L.DomEvent.on(elContainer, 'pointerdown touchstart mousedown', this.onElContainerPointerDown, this);
        L.DomEvent.on(elContainer, 'pointercancel touchcancel', this.onElContainerPointerUp, this);
        elContainer.addEventListener('keydown', this.onElContainerKeyPress.bind(this));
    }
    /**
     * Set the knob element's event listeners.
     * @param elKnob - The knob DOM element.
     */
    setElKnobEventListeners(elKnob) {
        elKnob.addEventListener('mouseover', this.onElKnobMouseover.bind(this));
        elKnob.addEventListener('mouseleave', this.onElKnobMouseleave.bind(this));
    }
    /**
     * The event handler for when the pointer is pressed down.
     * @param event - The pointer event.
     */
    onElContainerPointerDown(event) {
        L.DomEvent.preventDefault(event);
        L.DomEvent.stopPropagation(event);
        this.elContainer.focus();
        this.setKnobPositionFromPointerEvent(event);
        L.DomEvent.on(document.documentElement, 'pointermove touchmove mousemove', this.onElContainerPointerMove, this);
        L.DomEvent.on(document.documentElement, 'pointerup touchend mouseup', this.onElContainerPointerUp, this);
        if (this.onPointerDownCb) {
            this.onPointerDownCb({ event, state: this.state });
        }
    }
    /**
     * The event handler for when the pointer is moved.
     * @param event - The pointer event.
     */
    onElContainerPointerMove(event) {
        L.DomEvent.preventDefault(event);
        L.DomEvent.stopPropagation(event);
        this.setKnobPositionFromPointerEvent(event);
        if (this.onPointerMoveCb) {
            this.onPointerMoveCb({ event, state: this.state });
        }
    }
    /**
     * The event handler for when the pointer is lifted up.
     * @param event - The pointer event.
     */
    onElContainerPointerUp(event) {
        L.DomEvent.off(document.documentElement, 'pointermove touchmove mousemove', this.onElContainerPointerMove, this);
        L.DomEvent.off(document.documentElement, 'pointerup touchend mouseup', this.onElContainerPointerUp, this);
        if (this.onPointerUpCb) {
            this.onPointerUpCb({ event, state: this.state });
        }
    }
    /**
     * The event handler for when a key is pressed.
     * @param event - The keyboard event.
     */
    onElContainerKeyPress(event) {
        switch (event.key) {
            case 'Left':
            case 'ArrowLeft':
                this.moveLeft();
                break;
            case 'Right':
            case 'ArrowRight':
                this.moveRight();
                break;
        }
    }
    /**
     * The event handler for when the cursor hovers over the knob element.
     */
    onElKnobMouseover() {
        if (!this.options.isTooltipPermanent) {
            this.updateState({ tooltipOpacity: 1 });
        }
    }
    /**
     * The event handler for when the cursor leaves over the knob element.
     */
    onElKnobMouseleave() {
        if (!this.options.isTooltipPermanent) {
            this.updateState({ tooltipOpacity: 0 });
        }
    }
    /**
     * Set the knob position from the pointer event's information.
     * @param event - The pointer event.
     */
    setKnobPositionFromPointerEvent(event) {
        if (!(event instanceof MouseEvent))
            return;
        const mousePosition = L.DomEvent.getMousePosition(event, this.elContainer);
        const knobPositionRaw = mousePosition.x / (this.elContainer.clientWidth / 100);
        const knobPosition = Math.max(0, Math.min(knobPositionRaw, 100));
        // find tick closest to knobPosition
        const distance = (position, tick) => Math.abs(tick.position - position);
        const selectedTick = findClosestIndex(knobPosition, this.ticks, distance);
        this.updateState({ knobPosition, selectedTick });
    }
}
/**
 * Default slider options.
 */
Slider.optionsDefault = {
    shouldDrawTicks: false,
    isTooltipPermanent: false,
};
/**
 * The slider base class name.
 */
Slider.className = 'c-slider';
//# sourceMappingURL=Slider.js.map